/**
 * Keşif — live crawl panel.
 *
 * Four surfaces over one job: counters, the page stream, the host politeness
 * table and the asset gallery. Each answers a different question a user asks
 * mid-crawl — "is it working", "what did it find", "am I being rude to
 * anyone", "what did it download" — and separating them is what keeps the
 * politeness table from being buried under a thousand page rows.
 *
 * Everything reads from `useKesifStore`. The store owns the SSE subscription,
 * so mounting or unmounting a tab never touches the stream: a crawl outlives
 * this component by design (the engine runs server-side regardless of viewers).
 */
import { useEffect, useState } from "react";
import {
  Ban,
  Database,
  Download,
  FileWarning,
  Gauge,
  Image as ImageIcon,
  Layers,
  Link2,
  Pause,
  Play,
  RefreshCw,
  RotateCw,
  ShieldCheck,
  ShieldOff,
  Square,
  Trash2,
  Zap,
} from "lucide-react";
import {
  ASSET_KIND_LABELS,
  PAGE_TYPE_LABELS,
  PHASE_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  TIER_LABELS,
  formatBytes,
  formatDuration,
  isTerminalStatus,
  type CrawlAsset,
  type CrawlAssetKind,
  type CrawlHost,
  type CrawlJob,
  type CrawlPage,
} from "../../domain/kesif";
import {
  ASSETS_PAGE_SIZE,
  PAGES_PAGE_SIZE,
  useKesifStore,
} from "../../store/kesifStore";
import { Btn, Chip, EmptyState, ErrorBanner, Pager, SectionTitle } from "../library/ui";
import { DeleteJobModal } from "./DeleteJobModal";
import { RecrawlModal } from "./RecrawlModal";

type Tab = "pages" | "hosts" | "assets" | "log";

const TABS: readonly { id: Tab; label: string }[] = [
  { id: "pages", label: "Sayfalar" },
  { id: "hosts", label: "Hostlar" },
  { id: "assets", label: "Varlıklar" },
  { id: "log", label: "Kayıt" },
];

export function CrawlLivePanel({
  jobId,
  onBack,
  onOpenJob,
}: {
  jobId: string;
  onBack: () => void;
  /**
   * Başka bir işe geç. Store'daki `openJob` yetmez: hangi işin gösterileceğini
   * `?job=` belirler ve bu bileşen onu prop olarak alır, dolayısıyla store'u
   * tek başına değiştirmek bir sonraki effect turunda geri alınır.
   */
  onOpenJob: (id: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("pages");
  const job = useKesifStore((s) => s.job);
  const detailLoading = useKesifStore((s) => s.detailLoading);
  const detailError = useKesifStore((s) => s.detailError);
  const openJob = useKesifStore((s) => s.openJob);
  const closeJob = useKesifStore((s) => s.closeJob);
  const clearErrors = useKesifStore((s) => s.clearErrors);

  useEffect(() => {
    void openJob(jobId);
    // Detaching on unmount stops the SSE reader; it does NOT stop the crawl.
    return () => closeJob();
  }, [jobId, openJob, closeJob]);

  if (detailLoading && !job) {
    return (
      <div className="flex items-center gap-2 py-16 text-[13px] text-slate-500">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Tarama yükleniyor…
      </div>
    );
  }
  if (!job) {
    return (
      <div className="space-y-4">
        {detailError && <ErrorBanner message={detailError} onDismiss={clearErrors} />}
        <EmptyState
          title="Tarama bulunamadı"
          description="Bu iş silinmiş veya erişiminiz yok."
          action={<Btn onClick={onBack}>Listeye dön</Btn>}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {detailError && <ErrorBanner message={detailError} onDismiss={clearErrors} />}
      <JobHeader job={job} onBack={onBack} onOpenJob={onOpenJob} />
      <CounterGrid />
      <PlanAndSaturation />

      <div className="flex items-center gap-1 border-b border-white/[0.07]">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={[
              "-mb-px border-b-2 px-3 py-2 text-[12.5px] font-medium transition-colors",
              tab === t.id
                ? "border-violet-500 text-white"
                : "border-transparent text-slate-500 hover:text-slate-300",
            ].join(" ")}
          >
            {t.label}
            <TabCount tab={t.id} />
          </button>
        ))}
      </div>

      {tab === "pages" && <PagesTab />}
      {tab === "hosts" && <HostsTab />}
      {tab === "assets" && <AssetsTab />}
      {tab === "log" && <LogTab />}
    </div>
  );
}

function TabCount({ tab }: { tab: Tab }) {
  const pages = useKesifStore((s) => s.pagesTotal);
  const assets = useKesifStore((s) => s.assetsTotal);
  const hosts = useKesifStore((s) => s.hosts.length);
  const logs = useKesifStore((s) => s.logs.length);
  const n = tab === "pages" ? pages : tab === "assets" ? assets : tab === "hosts" ? hosts : logs;
  if (!n) return null;
  return <span className="ml-1.5 text-[10.5px] text-slate-600">{n}</span>;
}

/* ---------- header + controls ---------- */

function JobHeader({
  job,
  onBack,
  onOpenJob,
}: {
  job: CrawlJob;
  onBack: () => void;
  onOpenJob: (id: string) => void;
}) {
  const [dialog, setDialog] = useState<"recrawl" | "delete" | null>(null);
  const streaming = useKesifStore((s) => s.streaming);
  const reconnecting = useKesifStore((s) => s.reconnecting);
  const stopReason = useKesifStore((s) => s.stopReason);
  const metrics = useKesifStore((s) => s.metrics);
  const pause = useKesifStore((s) => s.pause);
  const resume = useKesifStore((s) => s.resume);
  const cancel = useKesifStore((s) => s.cancel);
  const start = useKesifStore((s) => s.start);
  const openJob = useKesifStore((s) => s.openJob);

  const terminal = isTerminalStatus(job.status);

  return (
    <header className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Btn tone="ghost" size="sm" onClick={onBack}>
              ← Liste
            </Btn>
            <h2 className="truncate text-[15px] font-semibold text-white">
              {job.name || job.seedUrls[0] || "Adsız tarama"}
            </h2>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-1">
            <Chip tone={STATUS_TONES[job.status]}>{STATUS_LABELS[job.status]}</Chip>
            {!terminal && <Chip tone="violet">{PHASE_LABELS[job.phase]}</Chip>}
            {streaming && !reconnecting && (
              <Chip tone="cyan">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-400" />
                canlı
              </Chip>
            )}
            {reconnecting && (
              <Chip tone="amber">
                yeniden bağlanıyor ({reconnecting.attempt}/{reconnecting.max})
              </Chip>
            )}
            {!job.policy.respectRobots && (
              <Chip tone="rose" title="robots.txt yok sayıldı — sahiplik beyan edilmiş">
                <ShieldOff className="h-2.5 w-2.5" />
                robots kapalı
              </Chip>
            )}
            {metrics.pagesPerMinute > 0 && (
              <Chip>{metrics.pagesPerMinute.toFixed(1)} sayfa/dk</Chip>
            )}
            {metrics.etaSeconds !== null && (
              <Chip title="Tahmin: paydası keşifle büyür, geriye gidebilir.">
                ~{formatDuration(metrics.etaSeconds)}
              </Chip>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {job.status === "running" && (
            <Btn tone="outline" size="sm" onClick={() => void pause(job.id)}>
              <Pause className="h-3.5 w-3.5" />
              Duraklat
            </Btn>
          )}
          {job.status === "paused" && (
            <Btn tone="primary" size="sm" onClick={() => void resume(job.id)}>
              <Play className="h-3.5 w-3.5" />
              Devam et
            </Btn>
          )}
          {job.status === "queued" && (
            <Btn tone="primary" size="sm" onClick={() => void start(job.id)}>
              <Play className="h-3.5 w-3.5" />
              Başlat
            </Btn>
          )}
          {terminal && (
            <Btn
              tone="primary"
              size="sm"
              onClick={() => setDialog("recrawl")}
              title="Sayfa bütçesini yükselt ve kaldığı yerden sürdür — ya da kopyala"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Yeniden tara
            </Btn>
          )}
          {!terminal && (
            <Btn tone="danger" size="sm" onClick={() => void cancel(job.id)}>
              <Square className="h-3.5 w-3.5" />
              İptal
            </Btn>
          )}
          <Btn tone="ghost" size="sm" onClick={() => void openJob(job.id)} aria-label="Yenile">
            <RefreshCw className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            tone="ghost"
            size="sm"
            onClick={() => setDialog("delete")}
            aria-label="Taramayı sil"
            title="Taramayı ve veri kümesini sil"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Btn>
        </div>
      </div>

      {dialog === "recrawl" && (
        <RecrawlModal
          job={job}
          onClose={() => setDialog(null)}
          onCloned={(id) => {
            setDialog(null);
            // Kopya YENİ bir iştir; panele onu getir, yoksa kullanıcı
            // taramanın başladığını hiç görmez.
            onOpenJob(id);
          }}
        />
      )}
      {dialog === "delete" && (
        <DeleteJobModal
          job={job}
          onClose={() => setDialog(null)}
          // Silinen işin paneli gösterilemez; listeye dön.
          onDeleted={onBack}
        />
      )}

      <ProgressBar />

      {(stopReason || job.degradedReason || job.error) && (
        <div className="space-y-1.5">
          {job.error && <ErrorBanner message={job.error} />}
          {stopReason && (
            <p className="text-[11.5px] text-slate-500">
              Durma nedeni: <span className="text-slate-300">{stopReason}</span>
            </p>
          )}
          {job.degradedReason && (
            <p className="text-[11.5px] text-amber-300/80">{job.degradedReason}</p>
          )}
        </div>
      )}
    </header>
  );
}

function ProgressBar() {
  const metrics = useKesifStore((s) => s.metrics);
  const counters = useKesifStore((s) => s.counters);
  const job = useKesifStore((s) => s.job);
  if (!job) return null;

  // Prefer the budget-relative fraction: it only ever moves forward, whereas
  // the server's `progress` uses `fetched / (fetched + pending)` and slides
  // BACKWARDS every time a page yields new links. Showing a bar that retreats
  // reads as a bug even though the number is honest.
  const budget = Math.max(1, job.policy.maxPages);
  const byBudget = Math.min(1, counters.fetched / budget);
  const pct = Math.round(Math.max(byBudget, metrics.progress * 0.999) * 100);

  return (
    <div className="space-y-1">
      <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[10.5px] text-slate-600">
        <span>
          {counters.fetched.toLocaleString("tr-TR")} / {budget.toLocaleString("tr-TR")} sayfa
        </span>
        <span>
          {counters.pending.toLocaleString("tr-TR")} kuyrukta ·{" "}
          {metrics.inFlight} uçuşta · {metrics.activeHosts} host
        </span>
      </div>
    </div>
  );
}

/* ---------- counters ---------- */

function CounterGrid() {
  const c = useKesifStore((s) => s.counters);
  const frontier = useKesifStore((s) => s.frontier);

  const cells = [
    { icon: Database, label: "Çekilen", value: c.fetched.toLocaleString("tr-TR") },
    { icon: Link2, label: "Keşfedilen", value: c.discovered.toLocaleString("tr-TR") },
    { icon: Layers, label: "Yinelenen", value: c.duplicate.toLocaleString("tr-TR") },
    { icon: FileWarning, label: "Hatalı", value: c.failed.toLocaleString("tr-TR") },
    { icon: Ban, label: "Atlanan", value: c.skipped.toLocaleString("tr-TR") },
    { icon: ImageIcon, label: "Varlık", value: c.assetsSaved.toLocaleString("tr-TR") },
    { icon: Download, label: "İndirilen", value: formatBytes(c.bytesFetched + c.assetBytes) },
    { icon: Zap, label: "LLM çağrısı", value: c.llmCalls.toLocaleString("tr-TR") },
    { icon: ShieldCheck, label: "Korpus", value: c.corpusRows.toLocaleString("tr-TR") },
    { icon: Gauge, label: "Host", value: c.hostsSeen.toLocaleString("tr-TR") },
  ];

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {cells.map((cell) => (
          <div
            key={cell.label}
            className="rounded-lg border border-white/[0.07] bg-white/[0.015] px-2.5 py-2"
          >
            <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500">
              <cell.icon className="h-3 w-3" />
              {cell.label}
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-white">{cell.value}</div>
          </div>
        ))}
      </div>
      <p className="text-[10.5px] text-slate-600">
        Kuyruk: {frontier.pending} bekliyor · {frontier.done} bitti · {frontier.failed} hata ·{" "}
        {frontier.skipped} atlandı
        {frontier.leased > 0 && ` · ${frontier.leased} kirada`}
      </p>
    </div>
  );
}

/* ---------- plan + saturation ---------- */

function PlanAndSaturation() {
  const plan = useKesifStore((s) => s.plan);
  const saturation = useKesifStore((s) => s.saturation);
  if (!plan && !saturation) return null;

  return (
    <div className="space-y-2">
      {saturation && (
        <div className="rounded-lg border border-cyan-500/25 bg-cyan-500/[0.07] px-3 py-2.5">
          <div className="text-[12px] font-medium text-cyan-200">
            Doygunluk: yeni içerik oranı %{Math.round(saturation.novelRatio * 100)}
          </div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-cyan-200/70">
            {saturation.reason || "Yeni sayfalar yeni bilgi taşımıyor; tarama erken durduruldu."}
          </p>
        </div>
      )}
      {plan && (
        <details className="rounded-lg border border-white/[0.07] bg-white/[0.015]">
          <summary className="cursor-pointer px-3 py-2 text-[12px] text-slate-300">
            LLM planı{plan.detectedSiteType ? ` — ${plan.detectedSiteType}` : ""}
          </summary>
          <div className="space-y-2 border-t border-white/[0.06] px-3 py-2.5">
            {plan.summary && (
              <p className="text-[11.5px] leading-relaxed text-slate-400">{plan.summary}</p>
            )}
            {plan.priorityKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {plan.priorityKeywords.slice(0, 20).map((k) => (
                  <Chip key={k} tone="violet">
                    {k}
                  </Chip>
                ))}
              </div>
            )}
            {plan.warnings.map((w) => (
              <p key={w} className="text-[11px] text-amber-300/80">
                {w}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/* ---------- pages ---------- */

function PagesTab() {
  const pages = useKesifStore((s) => s.pages);
  const total = useKesifStore((s) => s.pagesTotal);
  const view = useKesifStore((s) => s.pagesView);
  const loading = useKesifStore((s) => s.pagesLoading);
  const refresh = useKesifStore((s) => s.refreshPages);
  const setView = useKesifStore((s) => s.setPagesView);

  // The input is local so typing stays at 60 fps; the committed query lives in
  // the store because the SEARCH runs on the server. Filtering the loaded rows
  // instead would quietly mean "search the ~100 rows in the buffer" and report
  // "no results" for a term sitting on page 7.
  const [draft, setDraft] = useState(view.q);
  useEffect(() => {
    if (draft === view.q) return;
    const t = setTimeout(() => void setView({ q: draft.trim() }), 350);
    return () => clearTimeout(t);
  }, [draft, view.q, setView]);

  if (pages.length === 0 && view.q === "") {
    return (
      <EmptyState
        title="Henüz sayfa yok"
        description="Tarama başladığında çekilen sayfalar burada canlı olarak akar."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Başlık veya adres içinde ara…"
          className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-1.5 text-[12px] text-white placeholder:text-slate-600 focus:border-violet-500/60 focus:outline-none"
        />
        <Btn tone="ghost" size="sm" onClick={() => void refresh()} loading={loading}>
          {!loading && <RefreshCw className="h-3.5 w-3.5" />}
        </Btn>
      </div>
      {pages.length === 0 ? (
        <EmptyState
          title="Eşleşme yok"
          description={`«${view.q}» için bu taramada kayıtlı sayfa bulunamadı. Arama tüm taramada çalışır, yalnız görünen sayfada değil.`}
        />
      ) : (
        <>
          <ul className="divide-y divide-white/[0.05] overflow-hidden rounded-lg border border-white/[0.07]">
            {pages.map((p) => (
              <PageRow key={p.id} page={p} />
            ))}
          </ul>
          {total <= PAGES_PAGE_SIZE && (
            <p className="text-[10.5px] text-slate-600">
              {total.toLocaleString("tr-TR")} sayfa
            </p>
          )}
          <Pager
            offset={view.offset}
            limit={PAGES_PAGE_SIZE}
            total={total}
            busy={loading}
            unit="sayfa"
            onChange={(offset) => void setView({ offset })}
          />
        </>
      )}
    </div>
  );
}

function PageRow({ page }: { page: CrawlPage }) {
  const bad = page.error !== "" || (page.httpStatus !== null && page.httpStatus >= 400);
  return (
    <li className="flex items-start gap-3 px-3 py-2 hover:bg-white/[0.02]">
      <span
        className={[
          "mt-0.5 w-9 shrink-0 rounded px-1 py-0.5 text-center text-[10px] font-bold",
          bad ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300",
        ].join(" ")}
      >
        {page.httpStatus ?? "—"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] text-slate-200">
          {page.title || "(başlıksız)"}
        </div>
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer noopener"
          className="block truncate text-[11px] text-slate-600 hover:text-violet-300"
        >
          {page.url}
        </a>
        {page.error && (
          <p className="mt-0.5 truncate text-[11px] text-rose-300/80">{page.error}</p>
        )}
      </div>
      <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
        {page.duplicateOf && <Chip tone="amber">yinelenen</Chip>}
        {page.fetchTier !== "http" && <Chip tone="cyan">{TIER_LABELS[page.fetchTier]}</Chip>}
        <Chip>{PAGE_TYPE_LABELS[page.pageType]}</Chip>
        <Chip title="Kalite skoru (0-1)">{page.qualityScore.toFixed(2)}</Chip>
        <span className="w-16 text-right text-[10.5px] text-slate-600">
          {page.charCount.toLocaleString("tr-TR")} krk
        </span>
        <span className="w-12 text-right text-[10.5px] text-slate-600">
          {page.outlinkCount} link
        </span>
        <span className="w-9 text-right text-[10.5px] text-slate-600">d{page.depth}</span>
      </div>
    </li>
  );
}

/* ---------- hosts ---------- */

function HostsTab() {
  const hosts = useKesifStore((s) => s.hosts);
  if (hosts.length === 0) {
    return (
      <EmptyState
        title="Host bilgisi yok"
        description="robots.txt çözümlendikçe her host burada görünür."
      />
    );
  }
  return (
    <div className="space-y-3">
      <SectionTitle
        title="Nezaket durumu"
        hint="Etkin gecikme, sunucunun bildirdiği Crawl-delay ile sizin hız ayarınızın YAVAŞ olanıdır. 429/503 görüldüğünde otomatik artar."
      />
      <div className="overflow-x-auto rounded-lg border border-white/[0.07]">
        <table className="w-full text-[11.5px]">
          <thead className="bg-white/[0.03] text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Host</th>
              <th className="px-3 py-2 text-left font-medium">robots</th>
              <th className="px-3 py-2 text-right font-medium">Gecikme</th>
              <th className="px-3 py-2 text-right font-medium">Sitemap</th>
              <th className="px-3 py-2 text-right font-medium">Sayfa</th>
              <th className="px-3 py-2 text-right font-medium">Veri</th>
              <th className="px-3 py-2 text-right font-medium">Hata</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.05]">
            {hosts.map((h) => (
              <HostRow key={h.host} host={h} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HostRow({ host: h }: { host: CrawlHost }) {
  return (
    <tr className="hover:bg-white/[0.02]">
      <td className="px-3 py-2">
        <span className="font-mono text-slate-200">{h.host}</span>
        {h.circuitOpen && (
          <Chip tone="rose" title="Ardışık hatalar nedeniyle geçici olarak durduruldu">
            devre açık
          </Chip>
        )}
      </td>
      <td className="px-3 py-2">
        {h.allowed ? (
          <span className="text-emerald-300/90">
            {/* A 404 robots.txt is full permission under RFC 9309 — worth
                saying, because it looks like a failure in a status column. */}
            {h.robotsStatus === 404
              ? "yok (404) — tam izin"
              : h.robotsBypass
                ? "yok sayıldı"
                : `izinli${h.robotsStatus ? ` (${h.robotsStatus})` : ""}`}
          </span>
        ) : (
          <span className="text-rose-300/90" title={h.disallowReason}>
            engelli
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-slate-300">
        {h.effectiveDelayS.toFixed(1)} sn
        {h.crawlDelayS !== null && (
          <span className="ml-1 text-slate-600" title="Sunucunun bildirdiği Crawl-delay">
            ({h.crawlDelayS})
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-slate-400">{h.sitemapCount}</td>
      <td className="px-3 py-2 text-right text-slate-300">
        {h.pagesFetched.toLocaleString("tr-TR")}
      </td>
      <td className="px-3 py-2 text-right text-slate-400">{formatBytes(h.bytesFetched)}</td>
      <td className="px-3 py-2 text-right">
        <span className={h.consecutiveErrors > 0 ? "text-amber-300" : "text-slate-600"}>
          {h.consecutiveErrors}
        </span>
      </td>
    </tr>
  );
}

/* ---------- assets ---------- */

function AssetsTab() {
  const assets = useKesifStore((s) => s.assets);
  const total = useKesifStore((s) => s.assetsTotal);
  const view = useKesifStore((s) => s.assetsView);
  const loading = useKesifStore((s) => s.assetsLoading);
  const refresh = useKesifStore((s) => s.refreshAssets);
  const setView = useKesifStore((s) => s.setAssetsView);

  // `downloadUrl` must be non-empty to reach the grid. `<img src="">` does not
  // fail loudly — the HTML spec says do not fetch and mark the element broken,
  // so an empty URL renders a broken-image glyph with no request to debug.
  // Anything without a URL drops to the list, which states its status in words.
  const inGrid = (a: CrawlAsset) =>
    a.kind === "image" && a.status === "saved" && a.downloadUrl !== "";
  const images = assets.filter(inGrid);
  const others = assets.filter((a) => !inGrid(a));

  if (assets.length === 0 && view.kind === "") {
    return (
      <EmptyState
        title="Varlık yok"
        description="Görsel ve belgeler indirildikçe burada görünür. Politikada varlık toplama kapalıysa hiç görünmez."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => void setView({ kind: "" })}
          className={chipBtn(view.kind === "")}
        >
          Tümü
        </button>
        {/* Every kind is offered, not only the ones in the loaded window: the
            filter runs on the server, so a window-derived chip set would drop
            kinds that live on later pages — and once filtered it would erase
            every other chip and trap the reader on one kind. */}
        {Object.entries(ASSET_KIND_LABELS).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => void setView({ kind: k })}
            className={chipBtn(view.kind === k)}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto text-[10.5px] text-slate-600">
          {total.toLocaleString("tr-TR")} varlık
        </span>
        <Btn tone="ghost" size="sm" onClick={() => void refresh()} loading={loading}>
          {!loading && <RefreshCw className="h-3.5 w-3.5" />}
        </Btn>
      </div>

      {assets.length === 0 && (
        <EmptyState
          title="Bu türde varlık yok"
          description={`Bu taramada «${ASSET_KIND_LABELS[view.kind as CrawlAssetKind] ?? view.kind}» türünde kayıt bulunmuyor.`}
        />
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {images.map((a) => (
            <a
              key={a.id}
              href={a.downloadUrl}
              target="_blank"
              rel="noreferrer noopener"
              title={`${a.altText || a.url}\n${formatBytes(a.bytes)}`}
              className="group relative aspect-square overflow-hidden rounded-lg border border-white/[0.07] bg-black/30"
            >
              {/* Served through the ACL'd `/raw` route, never from webroot:
                  crawled third-party bytes must not be publicly re-hosted. */}
              <img
                src={a.downloadUrl}
                alt={a.altText}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
              <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1.5 py-0.5 text-[9.5px] text-slate-300 opacity-0 transition-opacity group-hover:opacity-100">
                {formatBytes(a.bytes)}
                {a.width && a.height ? ` · ${a.width}×${a.height}` : ""}
              </span>
            </a>
          ))}
        </div>
      )}

      {others.length > 0 && (
        <ul className="divide-y divide-white/[0.05] overflow-hidden rounded-lg border border-white/[0.07]">
          {others.map((a) => (
            <AssetRow key={a.id} asset={a} />
          ))}
        </ul>
      )}

      <Pager
        offset={view.offset}
        limit={ASSETS_PAGE_SIZE}
        total={total}
        busy={loading}
        unit="varlık"
        onChange={(offset) => void setView({ offset })}
      />
    </div>
  );
}

function chipBtn(active: boolean): string {
  return [
    "rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors",
    active
      ? "border-violet-500/40 bg-violet-500/15 text-violet-200"
      : "border-white/10 bg-white/[0.03] text-slate-500 hover:text-slate-300",
  ].join(" ");
}

function AssetRow({ asset: a }: { asset: CrawlAsset }) {
  const saved = a.status === "saved" && a.downloadUrl !== "";
  return (
    <li className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02]">
      <Chip tone={saved ? "green" : "amber"}>{ASSET_KIND_LABELS[a.kind]}</Chip>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[11.5px] text-slate-300">{a.url}</div>
        {!saved && (
          <p className="text-[10.5px] text-amber-300/80">
            {a.status}
            {a.skipReason ? ` — ${a.skipReason}` : ""}
          </p>
        )}
      </div>
      <span className="shrink-0 text-[10.5px] text-slate-600">{a.contentType}</span>
      <span className="w-16 shrink-0 text-right text-[10.5px] text-slate-500">
        {formatBytes(a.bytes)}
      </span>
      {saved && (
        <a
          href={a.downloadUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="shrink-0 text-slate-500 hover:text-violet-300"
          aria-label="İndir"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
      )}
    </li>
  );
}

/* ---------- log ---------- */

function LogTab() {
  const logs = useKesifStore((s) => s.logs);
  const streaming = useKesifStore((s) => s.streaming);
  if (logs.length === 0) {
    // Lines are not persisted and are never backfilled: they arrive on the
    // stream and live in this tab only. Saying so prevents an empty pane from
    // reading as "nothing happened" when it actually means "nothing happened
    // since you opened this panel".
    return (
      <EmptyState
        title="Kayıt yok"
        description={
          streaming
            ? "Satırlar bu paneli açtığınız andan itibaren birikir; öncesi saklanmaz. Hata, devre kesme ve bütçe olayları burada görünür — sayfa listesinin tamamı «Sayfalar» sekmesinde, denetim izi sunucu loglarındadır."
            : "Bu iş için canlı akış kapalı. Kayıt satırları yalnızca tarama sürerken akar; tamamlanmış bir işin ayrıntıları «Sayfalar» sekmesinde ve sunucu loglarındadır."
        }
      />
    );
  }
  return (
    <ul className="max-h-[420px] space-y-0.5 overflow-y-auto rounded-lg border border-white/[0.07] bg-black/20 p-2 font-mono text-[11px]">
      {logs.map((l) => (
        <li key={l.id} className="flex gap-2">
          <span className="shrink-0 text-slate-600">
            {new Date(l.at).toLocaleTimeString("tr-TR")}
          </span>
          <span className="min-w-0 flex-1">
            <span
              className={
                l.level === "error"
                  ? "text-rose-300"
                  : l.level === "warn"
                    ? "text-amber-300"
                    : "text-slate-400"
              }
            >
              {l.message}
            </span>
            {/* Failure lines carry the URL, and it is the whole point of the
                line: three identical "HTTP 404" entries say nothing without
                the address that produced them. */}
            {l.url && (
              <span className="block truncate text-slate-600" title={l.url}>
                {l.url}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
